import { useEffect, useState } from 'react';
import './RefLinksPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listRefLinks, createRefLink, deleteRefLink } from '../../api/reflinksAPI/reflinksAPI.js';
import useLang from '../../hooks/use-lang/useLang.js';

const SLUG_PATTERN = /^[a-z0-9_-]{1,64}$/;

function RefLinksPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [botUsername, setBotUsername] = useState(null);
  const [links, setLinks] = useState([]);
  const [sources, setSources] = useState([]);
  const [slug, setSlug] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    setError(null);
    listRefLinks()
      .then((data) => {
        setBotUsername(data.botUsername);
        setLinks(data.links);
        setSources(data.sources);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    const normalized = slug.trim().toLowerCase();
    if (!SLUG_PATTERN.test(normalized)) {
      setError(t('reflinks.invalidSlug', 'Only latin letters, digits, _ and - (max 64)'));
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await createRefLink({ slug: normalized, note: note.trim() || undefined });
      setSlug('');
      setNote('');
      load();
    } catch (err) {
      setError(err.payload?.error === 'SLUG_EXISTS' ? t('reflinks.exists', 'Link with this slug already exists') : err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(t('reflinks.deleteConfirm', 'Delete this link?'))) return;
    try {
      await deleteRefLink(link.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = link.url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopySource = async (source) => {
    try {
      await navigator.clipboard.writeText(source.url);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = source.url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedId(`src_${source.source}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const previewUrl = botUsername && SLUG_PATTERN.test(slug.trim().toLowerCase())
    ? `https://t.me/${botUsername}?start=${slug.trim().toLowerCase()}`
    : null;

  return (
    <div className="reflinks__page">
      <div className="page__container">
        <div className="reflinks__create">
          <input
            className="reflinks__input"
            type="text"
            placeholder={t('reflinks.slugPlaceholder', 'Slug, e.g. devhome')}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            maxLength={64}
          />
          <input
            className="reflinks__input"
            type="text"
            placeholder={t('reflinks.notePlaceholder', 'Note (optional)')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={255}
          />
          <button className="reflinks__create-btn" type="button" onClick={handleCreate} disabled={creating || !slug.trim()}>
            {t('reflinks.create', 'Create link')}
          </button>
        </div>
        {previewUrl && <p className="reflinks__preview">{previewUrl}</p>}
        {error && <p className="reflinks__error">{error}</p>}

        {loading ? (
          <Loader text={t('common.loading')} />
        ) : (
          <>
            <div className="reflinks__grid">
              {links.length === 0 && <p className="reflinks__empty">{t('reflinks.empty', 'No links yet')}</p>}
              {links.map((link) => (
                <div key={link.id} className="reflinks_card">
                  <div className="reflinks__head">
                    <span className="reflinks__slug">{link.slug}</span>
                    <button
                      className="reflinks__delete"
                      type="button"
                      onClick={() => handleDelete(link)}
                      aria-label={t('common.delete', 'Delete')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      </svg>
                    </button>
                  </div>
                  {link.note && <p className="reflinks__note">{link.note}</p>}
                  {link.url && (
                    <button className="reflinks__url" type="button" onClick={() => handleCopy(link)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      <span className="reflinks__url-text">{copiedId === link.id ? t('reflinks.copied', 'Copied!') : link.url}</span>
                    </button>
                  )}
                  <div className="reflinks__stats">
                    <div className="reflinks__stat">
                      <span className="reflinks__stat-value">{link.registrations}</span>
                      <span className="reflinks__stat-label">{t('reflinks.registrations', 'Registrations')}</span>
                    </div>
                    <div className="reflinks__stat">
                      <span className="reflinks__stat-value">{link.payingUsers}</span>
                      <span className="reflinks__stat-label">{t('reflinks.paying', 'Paying')}</span>
                    </div>
                    <div className="reflinks__stat">
                      <span className="reflinks__stat-value reflinks__stat-value--muted">{link.createdAtLabel}</span>
                      <span className="reflinks__stat-label">{t('reflinks.created', 'Created')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {sources.length > 0 && (
              <div className="reflinks__sources">
                <h3 className="reflinks__sources-title">{t('reflinks.sourcesTitle', 'Other sources')}</h3>
                {sources.map((s) => (
                  <div key={s.source} className="reflinks__source-card">
                    <div className="reflinks__head">
                      <span className="reflinks__slug">{s.source}</span>
                    </div>
                    {s.url && (
                      <button className="reflinks__url" type="button" onClick={() => handleCopySource(s)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <span className="reflinks__url-text">{copiedId === `src_${s.source}` ? t('reflinks.copied', 'Copied!') : s.url}</span>
                      </button>
                    )}
                    <div className="reflinks__stats">
                      <span className="reflinks__stat">
                        <span className="reflinks__stat-value">{s.registrations}</span>
                        <span className="reflinks__stat-label">{t('reflinks.registrations', 'Registrations')}</span>
                      </span>
                      <span className="reflinks__stat">
                        <span className="reflinks__stat-value">{s.payingUsers}</span>
                        <span className="reflinks__stat-label">{t('reflinks.paying', 'Paying')}</span>
                      </span>
                      <span className="reflinks__stat">
                        <span className="reflinks__stat-value reflinks__stat-value--muted">{s.lastRegisteredLabel || '—'}</span>
                        <span className="reflinks__stat-label">{t('reflinks.lastJoined', 'Last joined')}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RefLinksPage;
